import { useRef, useState } from 'react';
import { Camera, CheckCircle2, LoaderCircle, ScanLine } from 'lucide-react';

import { ApiError } from '@/api/client';
import { usePrescriptionOcr } from '@/api/hooks/useOcr';
import type { InventoryMatch, MatchedMedicine } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { cartActions } from '@/features/billing/cartStore';
import { money } from '@/lib/utils';

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1] ?? '');
    reader.onerror = () => reject(new Error('Could not read the image.'));
    reader.readAsDataURL(file);
  });
}

const CONFIDENCE_VARIANT: Record<string, 'ok' | 'low' | 'expiring'> = {
  high: 'ok',
  medium: 'expiring',
  low: 'low',
};

function MedicineLine({ line }: { line: MatchedMedicine }) {
  const [addedId, setAddedId] = useState<string | null>(null);
  const best = line.matches[0] as InventoryMatch | undefined;

  function addToCart(match: InventoryMatch) {
    const added = cartActions.addItem({
      medicineId: match.id,
      name: match.name,
      shortName: match.shortName ?? undefined,
      price: match.price,
      unit: match.unit,
      maxStock: match.stock,
      batchNo: match.batchNo,
      daysToExpiry: 9999,
      gstRate: match.gstRate,
    });
    if (added) setAddedId(match.id);
    else toast({ title: 'Already at max stock', description: match.name, variant: 'destructive' });
  }

  return (
    <div className="rounded-md border border-border p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">
            {line.name} {line.strength && <span className="text-muted-foreground">· {line.strength}</span>}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {[line.quantity ? `${line.quantity} units` : null, line.frequency, line.duration]
              .filter(Boolean)
              .join(' · ') || 'No dosage detail extracted'}
          </p>
        </div>
        <Badge variant={CONFIDENCE_VARIANT[line.confidence] ?? 'outline'} className="shrink-0 text-[10px]">
          {line.confidence} confidence
        </Badge>
      </div>

      {line.matches.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">No in-stock match found — search manually.</p>
      ) : (
        <div className="mt-2 space-y-1">
          {line.matches.slice(0, 3).map((match) => (
            <div key={match.id} className="flex items-center justify-between gap-2 rounded bg-secondary/50 px-2 py-1.5">
              <div className="min-w-0">
                <p className="truncate text-xs font-medium">{match.name}</p>
                <p className="text-[11px] text-muted-foreground">
                  {match.stock} {match.unit} in stock · {money(match.price)}
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                variant={addedId === match.id ? 'outline' : 'default'}
                onClick={() => addToCart(match)}
                disabled={addedId === match.id}
              >
                {addedId === match.id ? <CheckCircle2 className="h-3.5 w-3.5" /> : null}
                {addedId === match.id ? 'Added' : match === best ? 'Add best match' : 'Add'}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Photographs or uploads a prescription, transcribes it, and offers matched
 * in-stock medicines to add to the current bill — the pharmacist confirms
 * every line rather than anything being added automatically.
 */
export function PrescriptionScanDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const captureRef = useRef<HTMLInputElement>(null);
  const scan = usePrescriptionOcr();
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File) {
    setError(null);
    try {
      const imageBase64 = await fileToBase64(file);
      await scan.mutateAsync({ imageBase64, mimeType: file.type || 'image/jpeg' });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not read this prescription. Try another photo.');
    }
  }

  function handleClose(next: boolean) {
    if (!next) {
      scan.reset();
      setError(null);
    }
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-4 w-4" /> Scan prescription
          </DialogTitle>
        </DialogHeader>

        <input
          ref={captureRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            e.target.value = '';
          }}
        />
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            e.target.value = '';
          }}
        />

        {!scan.data && !scan.isPending && (
          <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-border py-10 text-center">
            <Camera className="h-8 w-8 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">Photograph or upload a prescription to get started.</p>
            <div className="flex gap-2">
              <Button type="button" onClick={() => captureRef.current?.click()}>
                Take photo
              </Button>
              <Button type="button" variant="outline" onClick={() => inputRef.current?.click()}>
                Upload photo
              </Button>
            </div>
          </div>
        )}

        {scan.isPending && (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <LoaderCircle className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Reading the prescription…</p>
          </div>
        )}

        {error && (
          <p role="alert" className="rounded-md border border-status-expired/30 bg-status-expired/10 px-3 py-2 text-sm text-status-expired">
            {error}
          </p>
        )}

        {scan.data && (
          <div className="max-h-96 space-y-3 overflow-y-auto">
            {(scan.data.doctor || scan.data.patient) && (
              <p className="text-xs text-muted-foreground">
                {scan.data.doctor && `Dr. ${scan.data.doctor}`}
                {scan.data.doctor && scan.data.patient && ' · '}
                {scan.data.patient && `Patient: ${scan.data.patient}`}
              </p>
            )}
            {scan.data.medicines.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No medicines could be read from this image.</p>
            ) : (
              scan.data.medicines.map((line, i) => <MedicineLine key={`${line.name}-${i}`} line={line} />)
            )}
          </div>
        )}

        <DialogFooter>
          {scan.data && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                scan.reset();
                setError(null);
              }}
            >
              Scan another
            </Button>
          )}
          <Button type="button" onClick={() => handleClose(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
